import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";

/**
 * PDP size toggles + CTAs. Purchase and inquiry happen in the drawers.
 */
export default class ProductPurchaseOptions extends Component {
  /** @type {"standard" | "custom" | "wtw" | null} */
  #activeToggle = null;

  /** @type {boolean} Set when leaving custom size, so the standard drawer opens after the close. */
  #reopenAsStandard = false;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener("click", this.#handleClick);

    document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    document.addEventListener(ThemeEvents.customSizeExit, this.#handleCustomSizeExit);
    document.addEventListener(ThemeEvents.customSizePriceChange, this.#updateHeaderPrice);
    document.addEventListener(ThemeEvents.drawerClose, this.#handleDrawerClose);

    if (this.dataset.defaultToggle) {
      this.#activeToggle = this.dataset.defaultToggle;
    } else if (new URL(window.location.href).searchParams.has("variant")) {
      this.#activeToggle = "standard";
    } else {
      const firstToggle = this.querySelector("[data-toggle]");
      this.#activeToggle = firstToggle?.dataset.toggle ?? null;
    }

    this.#refreshUi();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener("click", this.#handleClick);
    document.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    document.removeEventListener(ThemeEvents.customSizeExit, this.#handleCustomSizeExit);
    document.removeEventListener(ThemeEvents.customSizePriceChange, this.#updateHeaderPrice);
    document.removeEventListener(ThemeEvents.drawerClose, this.#handleDrawerClose);
  }

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const toggle = event.target.closest("[data-toggle]");
    if (toggle && this.contains(toggle)) {
      this.#handleToggle(toggle.dataset.toggle);
      return;
    }

    const cta = event.target.closest("[data-select-size-cta], [data-customize-cta]");
    if (!cta || !this.contains(cta)) return;

    const mode = cta.hasAttribute("data-customize-cta") ? "custom" : "standard";

    if (mode !== this.#activeToggle) {
      this.#activeToggle = mode;
      this.#refreshUi();
    }

    this.#openDrawerForMode(mode);
  };

  #handleToggle(mode) {
    if (!mode) return;

    if (mode !== this.#activeToggle) {
      const previousMode = this.#activeToggle;
      this.#activeToggle = mode;

      if (previousMode === "standard") {
        this.#resetStandardSize();
      } else if (previousMode === "custom") {
        this.#resetCustomSize();
      }

      this.#refreshUi();
    }

    this.#openDrawerForMode(mode);
  }

  #openDrawerForMode(mode) {
    if (mode === "standard") {
      this.#openDrawer("variant-picker", this.dataset.drawerTitleStandard);
    } else if (mode === "custom") {
      this.#openDrawer("custom-size-configurator", this.dataset.drawerTitleCustom);
    }
  }

  #handleCustomSizeExit = (event) => {
    const { productId } = event.detail ?? {};
    if (productId && String(productId) !== String(this.dataset.productId)) return;

    this.#activeToggle = "standard";
    this.#resetCustomSize();
    this.#refreshUi();
    this.#reopenAsStandard = true;
  };

  /** The exit button also closes the drawer, so the standard view opens once that finishes. */
  #handleDrawerClose = (event) => {
    if (!this.#reopenAsStandard) return;
    if (event.detail?.view !== "custom-size-configurator") return;

    this.#reopenAsStandard = false;

    // The closing view is moved back out immediately after this event.
    requestAnimationFrame(() => this.#openDrawerForMode("standard"));
  };

  #updateHeaderPrice = (event) => {
    const eventProductId = event?.detail?.productId;
    if (eventProductId && String(eventProductId) !== String(this.dataset.productId)) return;

    const display = document.querySelector(`product-price-display[data-product-id="${this.dataset.productId}"]`);
    if (!display || typeof display.showCustomPrice !== "function") return;

    const configurator = this.#activeToggle === "custom" ? this.#getConfigurator() : null;
    const current = configurator?.currentPrice;

    if (current) {
      display.showCustomPrice(current.price, current.isMinimum);
    } else {
      display.restore();
    }
  };

  /** @param {import("@theme/events").VariantUpdateEvent} event */
  #handleVariantUpdate = (event) => {
    const { resource, data } = event.detail;
    if (!resource) return;

    if (data.newProduct) {
      this.dataset.productId = String(data.newProduct.id);
    } else if (String(data.productId) !== String(this.dataset.productId)) {
      return;
    }

    this.#activeToggle = "standard";
    this.#resetCustomSize();
    this.#refreshUi();
  };

  #resetStandardSize() {
    const picker = document.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
    if (picker && typeof picker.reset === "function") {
      picker.reset();
    }

    const url = new URL(window.location.href);
    if (url.searchParams.has("variant")) {
      url.searchParams.delete("variant");
      history.replaceState({}, "", url.toString());
    }
  }

  #resetCustomSize() {
    this.#getConfigurator()?.reset?.();
  }

  /** @returns {HTMLElement & { reset?: Function, currentPrice?: { price: string, isMinimum: boolean } | null } | null} */
  #getConfigurator() {
    return document.querySelector(`custom-size-configurator[data-product-id="${this.dataset.productId}"]`);
  }

  #updateToggleStates() {
    const toggles = this.querySelectorAll("[data-toggle]");
    for (const toggle of toggles) {
      const isActive = toggle.dataset.toggle === this.#activeToggle;
      toggle.setAttribute("aria-pressed", String(isActive));
    }
  }

  #updateDeliveryInfo() {
    const deliveryInfo = this.querySelector("[data-delivery-info]");
    const deliveryText = deliveryInfo?.querySelector("[data-delivery-text]");
    if (!deliveryInfo || !deliveryText) return;

    const { availableInSelectedSizes = "", madeToOrderDelivery = "", customDelivery = "" } = deliveryInfo.dataset;

    let text = madeToOrderDelivery;
    let isSelection = false;

    if (this.#activeToggle === "wtw") {
      // Wall-to-wall is quoted per project, so no delivery promise applies.
      text = "";
    } else if (this.#activeToggle === "custom") {
      text = customDelivery;
    } else if (availableInSelectedSizes) {
      text = availableInSelectedSizes;
      isSelection = true;
    }

    deliveryText.textContent = text;

    // Markets without delivery copy for the active mode hide the line entirely.
    const hasText = text !== "";
    deliveryInfo.classList.toggle("hidden", !hasText);
    deliveryInfo.classList.toggle("flex", hasText);

    const brand = deliveryInfo.querySelector("[data-delivery-brand]");
    brand?.classList.toggle("hidden", !isSelection);

    const icon = deliveryInfo.querySelector("[data-delivery-icon]");
    icon?.classList.toggle("hidden", !isSelection);
    icon?.classList.toggle("flex", isSelection);
  }

  #refreshUi() {
    this.#updateToggleStates();
    this.#updateCta();
  }

  #updateCta() {
    this.#updateDeliveryInfo();
    this.#updateHeaderPrice();

    const selectSizeCta = this.querySelector("[data-select-size-cta]");
    const quoteLink = this.querySelector("[data-quote-link]");
    const customizeCta = this.querySelector("[data-customize-cta]");

    const isCustomMode = this.#activeToggle === "custom";
    const isWtwMode = this.#activeToggle === "wtw";
    const isStandardMode = !isCustomMode && !isWtwMode;

    if (selectSizeCta) {
      selectSizeCta.classList.toggle("hidden", !isStandardMode);
      selectSizeCta.classList.toggle("flex", isStandardMode);
    }

    if (customizeCta) {
      customizeCta.classList.toggle("hidden", !isCustomMode);
      customizeCta.classList.toggle("flex", isCustomMode);
    }

    if (quoteLink) {
      quoteLink.classList.toggle("hidden", !isWtwMode);
      quoteLink.classList.toggle("flex", isWtwMode);
    }
  }

  #openDrawer(view, title) {
    const drawer = document.querySelector("drawer-component:not([id])");
    if (!drawer || typeof drawer.openFromTrigger !== "function") return;

    const trigger = document.createElement("button");
    trigger.setAttribute("data-drawer-view", view);
    if (title) {
      trigger.setAttribute("data-drawer-title", title);
    }

    drawer.openFromTrigger(trigger);
  }
}

if (!customElements.get("product-purchase-options")) {
  customElements.define("product-purchase-options", ProductPurchaseOptions);
}
